import { AppConfig } from '@dh-io-emh/config-types';
import { IDealerAutosuggestion, IDealerBase, IDealerComplete } from '../models/dealer.model';
import { createHttpService } from '../utils/http.util';

type IDealerSearchCoordinates = {
  latitude: number;
  longitude: number;
};

type IDealerSearchResult = {
  coordinates: IDealerSearchCoordinates;
  results: IDealerBase[];
  searchTerm: string;
  total: number;
};

type IDealerSingleSearchResult = IDealerSearchResult & {
  results: IDealerComplete[];
};

type IDealerAutosuggestionsResult = {
  suggestions: IDealerAutosuggestion[];
};

type DealerSearchParams = {
  configurationExternalId?: string;
  country: string;
  expand: boolean;
  fields: string;
  location: string;
  page: number;
  size: number;
  searchProfileName?: string;
  sortProperty: string;
  blackList?: string;
};

export type DealerApi = ReturnType<typeof createDealerApi>;

export const createDealerApi = ({ config: { dealerLocator, lang } }: { config: AppConfig }) => {
  const http = createHttpService({
    url: `${dealerLocator.baseUrl}`,
    headers: {
      'X-APIKey': dealerLocator.widgetKey as string,
    },
    params: { lang },
  });

  return {
    getDealersByLocation: (locationId: string, distance: string, country: string) =>
      http
        .get<IDealerSearchResult>('dealers/search', {
          params: {
            locationId,
            marketCode: country,
            distance: distance.replace(/\s/g, '').toLowerCase(),
            sortProperty: 'distance',
            fields: 'baseInfo.externalId',
            filters: 'functions.activityCode=SALES,functions.brandCode=MB',
          },
        })
        .then((res) => res.data),

    getDealersById: (
      id: string | string[],
      localeLanguage = false,
      fields: string[] = ['baseInfo', 'address', 'brands'],
    ): Promise<IDealerComplete[]> =>
      http
        .get<IDealerSingleSearchResult>('dealers/search/byId', {
          params: {
            id,
            fields,
            localeLanguage,
          },
        })
        .then((res) => {
          return res?.data.results || [];
        })
        .catch(() => {
          return [];
        }),

    getAutosuggestions: (address: string, country: string) =>
      http
        .get<IDealerAutosuggestionsResult>('geocoding/autocomplete', {
          params: {
            marketCode: country,
            maxResults: 5,
            address,
          },
        })
        .then((res) => res.data),

    getNearestDealer: (
      zipcode: string,
      country: string,
      searchProfileName?: string,
      configId?: string,
      useBlackList?: boolean,
    ): Promise<IDealerSearchResult> => {
      const params: DealerSearchParams = {
        country,
        expand: true,
        fields: 'baseInfo,address,brands',
        location: zipcode,
        page: 1,
        size: 1,
        sortProperty: 'distance',
      };

      if (searchProfileName) {
        params.searchProfileName = searchProfileName;
      }

      if (configId) {
        params.configurationExternalId = configId;
      }

      if (useBlackList) {
        params.blackList =
          dealerLocator.blackListDealers && dealerLocator.blackListDealers?.length > 0
            ? dealerLocator.blackListDealers.toString()
            : undefined;
      }

      return http
        .get<IDealerSearchResult>('dealers/search', {
          params: params,
        })
        .then((res) => res.data);
    },
  };
};
